import api from './api';
import type { ApiResponse } from '@/types';

/**
 * Rooms / Salla registry API module (structural clone of the confirmation-status
 * slice of `appointment-workflow-api.ts`). Every endpoint sits behind
 * `feature:rooms_enabled` (404 when the flag is off):
 *
 *   GET    /rooms                 (appointments.view)              — ALL rooms, active + retired, ordered by sort_order
 *   POST   /rooms                 (settings.appointment_workflow)
 *   PUT    /rooms/{id}            (settings.appointment_workflow)
 *   DELETE /rooms/{id}            (settings.appointment_workflow)  — soft-retire (is_active = false)
 *   POST   /rooms/reorder {ids}   (settings.appointment_workflow)
 *
 * `Room` is declared here (rather than imported from `@/types`) so this module
 * — and the settings UI that consumes it — is self-contained; it mirrors
 * RoomResource one-for-one.
 */

export interface Room {
  id: string;
  name: string;
  abbreviation: string;
  color: string;
  capacity: number;
  sort_order: number;
  // RoomResource returns the raw users.id BIGINT FK (JSON number), not a cast
  // string. Typed as number|string so consumers don't assume string equality.
  default_user_id: number | string | null;
  turnover_minutes: number;
  is_active: boolean;
}

export interface CreateRoomData {
  name: string;
  abbreviation: string;
  color: string;
  capacity: number;
  // Form submits either a numeric string (Select onChange) or the untouched
  // JSON number seeded from the Room; Laravel's `integer` rule accepts both.
  default_user_id?: number | string | null;
  turnover_minutes: number;
  sort_order?: number;
  is_active?: boolean;
}

export interface UpdateRoomData extends Partial<CreateRoomData> {}

let roomsCache: Room[] | null = null;

/**
 * Module-level cache — the settings editor, the appointment-form room picker and
 * the calendar all read rooms; a save/delete/reorder invalidates it so every
 * consumer sees fresh data on next read.
 */
export const getRooms = async (
  options: { force?: boolean } = {},
): Promise<Room[]> => {
  if (!options.force && roomsCache) {
    return roomsCache;
  }
  const response = await api.get<ApiResponse<Room[]>>('/rooms');
  roomsCache = response.data.data;
  return roomsCache;
};

const invalidateRoomsCache = (): void => {
  roomsCache = null;
};

export const createRoom = async (data: CreateRoomData): Promise<Room> => {
  const response = await api.post<ApiResponse<Room>>('/rooms', data);
  invalidateRoomsCache();
  return response.data.data;
};

export const updateRoom = async (id: string, data: UpdateRoomData): Promise<Room> => {
  const response = await api.put<ApiResponse<Room>>(`/rooms/${id}`, data);
  invalidateRoomsCache();
  return response.data.data;
};

/** Soft-retire (is_active = false) — rooms are never hard-deleted. */
export const deleteRoom = async (id: string): Promise<void> => {
  await api.delete(`/rooms/${id}`);
  invalidateRoomsCache();
};

/** POST .../reorder returns only a message (no data) — refetch to repopulate the cache. */
export const reorderRooms = async (orderedIds: string[]): Promise<Room[]> => {
  await api.post('/rooms/reorder', { ids: orderedIds });
  invalidateRoomsCache();
  return getRooms({ force: true });
};
